import { PropsWithChildren, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { getPanicData } from "../../../../api/panicControl";
import { createDeviceId } from "../../../../utils/device";
import { register as registerEndpoint } from "../../../../api";
import { AxiosError } from "axios";
import { useToast } from "../../../../hooks/useToast";
import TextInput from "../../../../components/TextInput";
import { MenuItem } from "@mui/material";
import SelectInput from "../../../../components/SelectInput";
import Feedback from "../../../../components/Feedback";
import { useNavigate } from "react-router-dom";
import { useSession } from "../../../../hooks/useSession";
import Button from "../../../../components/Button";

const validationSchema = Yup.object().shape({
  email: Yup.string().email("E-mail inválido").required("Campo obrigatório"),
  password: Yup.string().required("Campo obrigatório"),
  full_name: Yup.string().required("Campo obrigatório"),
  phone_number: Yup.string(),
  city: Yup.string().required("Campo obrigatório"),
  state_of_residence: Yup.string().required("Campo obrigatório"),
  area_of_expertise_or_course: Yup.string().required("Campo obrigatório"),
  university_or_institution: Yup.string().required("Campo obrigatório"),
  affiliation_with_municipality_or_secretariat: Yup.string().required(
    "Campo obrigatório"
  ),
});

const fields = {
  email: {
    label: "E-mail",
    required: true,
    type: "email",
    autoComplete: "email",
    placeholder: "nome@instituicao.edu.br…",
  },
  password: {
    label: "Senha",
    required: true,
    type: "password",
    autoComplete: "new-password",
    placeholder: "Crie uma senha…",
  },
  full_name: {
    label: "Nome completo",
    required: true,
    autoComplete: "name",
    placeholder: "Nome e sobrenome…",
  },
  phone_number: {
    type: "tel",
    label: "Telefone celular",
    autoComplete: "tel",
    placeholder: "(27) 99999-0000…",
  },
  city: {
    label: "Cidade",
    required: true,
    autoComplete: "address-level2",
    placeholder: "Vitória…",
  },
  state_of_residence: {
    type: "select",
    label: "Estado de residência",
    required: true,
    autoComplete: "address-level1",
    options: [
      "AC",
      "AL",
      "AP",
      "AM",
      "BA",
      "CE",
      "DF",
      "ES",
      "GO",
      "MA",
      "MT",
      "MS",
      "MG",
      "PA",
      "PB",
      "PR",
      "PE",
      "PI",
      "RJ",
      "RN",
      "RS",
      "RO",
      "RR",
      "SC",
      "SP",
      "SE",
      "TO",
    ],
  },
  area_of_expertise_or_course: {
    label: "Área de atuação ou curso",
    required: true,
    placeholder: "Arquitetura e urbanismo…",
  },
  university_or_institution: {
    label: "Universidade ou instituição",
    required: true,
    placeholder: "UFES, UVV…",
  },
  affiliation_with_municipality_or_secretariat: {
    type: "select",
    label: "Vínculo com prefeitura ou secretaria",
    required: true,
    options: ["Sim", "Não"],
  },
};

interface Props {
  isLoading: boolean;
  setLoading: (isLoading: boolean) => void;
}

export default function Register(props: PropsWithChildren<Props>) {
  const { isLoading, setLoading, children } = props;
  const { toast } = useToast();
  const [isSignUpPending, setSignUpPending] = useState(false);
  const { formState, control, handleSubmit } = useForm({
    resolver: yupResolver(validationSchema),
  });
  const navigate = useNavigate();
  const { fetchProfile } = useSession();

  const signUp = async (values: any) => {
    try {
      setLoading(true);
      const panic = await getPanicData(false);
      const deviceId =
        localStorage.getItem("deviceId") ?? (await createDeviceId());
      await registerEndpoint(values, deviceId);
      localStorage.setItem("deviceId", deviceId);
      if (!panic.CadastroPublico) {
        setSignUpPending(true);
      } else {
        await fetchProfile();
        navigate("/home");
      }
    } catch (error) {
      let errorMessage =
        "Não foi possível concluir o cadastro. Tente novamente.";
      if (error instanceof AxiosError) {
        const serverError = error.response?.data;
        if (serverError && serverError.error && serverError.message) {
          errorMessage = serverError.message;
        }
      }
      toast({
        message: errorMessage,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (isSignUpPending) {
    return (
      <>
        <Feedback
          text="Cadastro realizado"
          subtitle="Sua conta está em análise. Você receberá um e-mail quando for aprovada."
        />
        {children}
      </>
    );
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit(signUp)}>
      {Object.entries(fields).map(([key, field]) => (
        <div key={`field-${key}`}>
          {(!(field as any).type ||
            (field as any).type === "tel" ||
            (field as any).type === "email" ||
            (field as any).type === "password") && (
            <Controller
              name={key as keyof typeof fields}
              control={control}
              rules={{
                required: (field as any).required,
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextInput
                  id={key}
                  name={key}
                  label={field.label}
                  required={(field as any).required}
                  variant="outlined"
                  type={(field as any).type === "select" ? "text" : (field as any).type}
                  autoComplete={(field as any).autoComplete}
                  placeholder={(field as any).placeholder}
                  spellCheck={(field as any).type === "email" ? false : undefined}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value ?? ""}
                />
              )}
            />
          )}

          {(field as any).type === "select" && (
            <Controller
              name={key as keyof typeof fields}
              control={control}
              rules={{
                required: (field as any).required,
              }}
              render={({ field: { onChange, value } }) => (
                <SelectInput
                  id={key}
                  name={key}
                  label={field.label}
                  required={(field as any).required}
                  value={value ?? ""}
                  displayEmpty
                  onChange={(e) => onChange(e.target.value)}
                >
                  <MenuItem disabled value="">
                    Selecione…
                  </MenuItem>
                  {(field as any).options.map((option: string) => (
                    <MenuItem key={`select-${key}-${option}`} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </SelectInput>
              )}
            />
          )}

          {formState.errors &&
            formState.errors[key as keyof typeof fields]?.message && (
              <p className="mt-1 text-sm text-red" role="alert">
                {formState.errors[key as keyof typeof fields]?.message}
              </p>
            )}
        </div>
      ))}

      <Button type="submit" disabled={isLoading}>
        {isLoading ? "Enviando…" : "Criar conta"}
      </Button>

      {children}
    </form>
  );
}
